import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Eye, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Footer from '../components/common/Footer';
import workplaceNewsApi from '../services/workplaceNewsApi';

const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 90px; /* 为固定导航栏留出空间 */
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 40px;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const NewsCard = styled.div`
  padding: 0;
  margin-bottom: 0;
`;

const NewsHeader = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const NewsTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.3;
  margin: 0 0 16px 0;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #718096;
  font-size: 14px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: #a0aec0;
`;

const NewsContent = styled.div`
  font-size: 16px;
  color: #374151;
  line-height: 1.8;
  padding-top: 24px;

  /* Markdown 样式 */
  p {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    color: #1a202c;
    line-height: 1.3;
  }

  h1 {
    font-size: 28px;
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 8px;
  }

  h2 {
    font-size: 24px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 6px;
  }

  h3 {
    font-size: 20px;
  }

  h4 {
    font-size: 18px;
  }

  ul, ol {
    margin: 16px 0;
    padding-left: 24px;
  }

  li {
    margin: 8px 0;
  }

  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 14px;
  }

  pre {
    background: #f3f4f6;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;

    code {
      background: none;
      padding: 0;
    }
  }

  blockquote {
    border-left: 4px solid #667eea;
    padding-left: 16px;
    margin: 16px 0;
    color: #4a5568;
    font-style: italic;
  }

  a {
    color: #667eea;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  strong {
    font-weight: 600;
    color: #1a202c;
  }

  em {
    font-style: italic;
    
    /* 图片说明文字样式（独立成段的斜体文字） */
    &:only-child {
      display: block;
      text-align: center;
      color: #718096;
      font-size: 14px;
      margin-top: -16px;
      margin-bottom: 24px;
    }
  }

  hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 24px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;

    th, td {
      border: 1px solid #e5e7eb;
      padding: 8px 12px;
      text-align: left;
    }

    th {
      background: #f9fafb;
      font-weight: 600;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 0;
    margin: 24px 0;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
    display: block;
    margin-left: auto;
    margin-right: auto;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 15px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    /* 封面图片样式 */
    &[alt*="AI岗位需求持续增长"] {
      width: 100%;
      max-width: 800px;
      margin-top: 0;
      margin-bottom: 32px;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 16px;
  color: #718096;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #dc2626;
`;



export default function WorkplaceNewsDetailPage() {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastViewedIdRef = React.useRef(null);
  
  const processMarkdownContent = (content) => {
    if (!content) return '';
    const lines = content.split('\n');
    if (lines[0] && lines[0].trim().startsWith('# ')) {
      return lines.slice(1).join('\n').trim();
    }
    return content;
  };

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        
        const newsId = id.startsWith('news_') ? id : id;
        
        // 检查是否已经为该ID增加过浏览量，避免 React StrictMode 重复调用
        const shouldIncrementView = lastViewedIdRef.current !== newsId;
        if (shouldIncrementView) {
          lastViewedIdRef.current = newsId;
        }
        
        const data = await workplaceNewsApi.getNewsById(newsId, shouldIncrementView);
        
        if (data.success && data.data) {
          setNews(data.data);
          } else {
            setError('未找到该资讯');
        }
      } catch (err) {
        console.error('获取资讯失败:', err);
        setError('加载资讯失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '未知时间';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };


  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <LoadingContainer>加载中...</LoadingContainer>
        </ContentWrapper>
        <Footer showDisclaimer={false} />
      </Container>
    );
  }

  if (error || !news) {
    return (
      <Container>
        <ContentWrapper>
          <ErrorContainer>
            <p>{error || '未找到该资讯'}</p>
          </ErrorContainer>
        </ContentWrapper>
        <Footer showDisclaimer={false} />
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <NewsCard>
          <NewsHeader>
            <NewsTitle>{news.title}</NewsTitle>
            <MetaInfo>
              <MetaItem>
                <IconWrapper>
                  <Calendar size={16} />
                </IconWrapper>
                发布时间：{formatDate(news.published_at || news.created_at)}
              </MetaItem>
              <MetaItem>
                <IconWrapper>
                  <Eye size={16} />
                </IconWrapper>
                浏览量：{news.view_count || 0}
              </MetaItem>
            </MetaInfo>
          </NewsHeader>

          <NewsContent>
            {news.content ? (
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ node, ...props }) => {
                    // 确保图片路径正确，如果是相对路径则添加PUBLIC_URL
                    const src = props.src?.startsWith('/') 
                      ? `${process.env.PUBLIC_URL || ''}${props.src}` 
                      : props.src;
                    
                    // 如果是PNG图片，提供WebP格式支持
                    if (src && src.toLowerCase().endsWith('.png')) {
                      const webpSrc = src.replace(/\.png$/i, '.webp');
                      return (
                        <picture>
                          {/* WebP格式：更好的压缩率和质量 */}
                          <source srcSet={webpSrc} type="image/webp" />
                          {/* PNG格式：作为fallback，兼容不支持WebP的浏览器 */}
                          <img {...props} src={src} alt={props.alt || ''} loading="lazy" decoding="async" />
                        </picture>
                      );
                    }
                    
                    // 非PNG图片保持原样
                    return (
                      <img {...props} src={src} alt={props.alt || ''} loading="lazy" decoding="async" />
                    );
                  },
                }}
              >
                {processMarkdownContent(news.content)}
              </ReactMarkdown>
            ) : (
              '暂无详细内容'
            )}
          </NewsContent>
        </NewsCard>
      </ContentWrapper>
      
      <Footer showDisclaimer={false} />
    </Container>
  );
}
